import { RESIDENT_LIBERTY_ROLES } from '../constants/residentConstants'
import type {
  ResidentListLabelMaps,
  RentalStatusMappingNode,
  ResidentStatusListMappings,
  ResidentStatusListResponse,
  RentalListResponse,
  SelectOption,
} from '../types'

export const EMPTY_RESIDENT_LIST_LABEL_MAPS: ResidentListLabelMaps = {}
export const EMPTY_RESIDENT_STATUS_LIST_MAPPINGS: ResidentStatusListMappings = {}

const NON_LIBERTY_COLLAPSED_CLAIM_STATUS_CODES = [
  'EXTERNAL_REVIEW',
  'PAYMENT_PENDING',
  'RE_OPENED',
] as const

export function normalizeConstantKey(value?: string | null): string {
  return String(value || '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_')
}

export function lookupConstantLabel(
  labels: Record<string, string> | undefined,
  value?: string | null,
  emptyLabel = '',
): string {
  const raw = String(value ?? '').trim()
  if (!raw) return emptyLabel

  const normalized = normalizeConstantKey(raw)
  return labels?.[normalized] ?? labels?.[raw] ?? raw
}

export function constantsToSelectOptions(
  labels?: Record<string, string>,
): SelectOption[] {
  if (!labels) return []

  return Object.entries(labels).map(([value, label]) => ({
    value,
    label,
  }))
}

export function getResidentStatusTree(
  statusList?: ResidentStatusListResponse | null,
): Record<string, RentalStatusMappingNode> {
  const mapping = statusList?.resident_status_mapping
  if (!mapping || typeof mapping !== 'object') return {}
  return mapping
}

export function getReimbursementStatusTree(
  statusList?: ResidentStatusListResponse | null,
): Record<string, RentalStatusMappingNode> {
  const mapping = statusList?.reimbursement_status_mapping
  if (!mapping || typeof mapping !== 'object') return {}
  return mapping
}

/** Legacy `RentalListResponse` shape from `/api/status_list` for hydration and progress sync. */
export function buildRentalListFromStatusList(
  statusList?: ResidentStatusListResponse | null,
): RentalListResponse {
  return {
    rental_status: getResidentStatusTree(statusList),
    reason: statusList?.reimbursement_reason ?? {},
  }
}

/** Reason for Incident / `reason_for_default` options from GET /api/status_list. */
export function buildReimbursementReasonSelectOptions(
  statusList?: ResidentStatusListResponse | null,
  rentalList?: RentalListResponse | null,
): SelectOption[] {
  const labels = statusList?.reimbursement_reason ?? rentalList?.reason ?? {}
  return constantsToSelectOptions(labels)
}

export function parseStatusListLabelMaps(
  statusList?: ResidentStatusListResponse | null,
): ResidentListLabelMaps {
  if (!statusList) return EMPTY_RESIDENT_LIST_LABEL_MAPS

  return {
    pay_status: statusList.pay_status,
    resident_rental_status: statusList.resident_rental_status,
    resident_rental_progress: statusList.resident_rental_progress,
    reimbursement_type: statusList.reimbursement_type,
    reimbursement_rental_status: statusList.reimbursement_rental_status,
    reimbursement_rental_progress: statusList.reimbursement_rental_progress,
    reimbursement_reason: statusList.reimbursement_reason,
    claim_status_label: statusList.claim_status_label,
  }
}

export function parseStatusListMappings(
  statusList?: ResidentStatusListResponse | null,
): ResidentStatusListMappings {
  if (!statusList) return EMPTY_RESIDENT_STATUS_LIST_MAPPINGS

  return {
    resident_status_mapping: statusList.resident_status_mapping,
    reimbursement_status_mapping: statusList.reimbursement_status_mapping,
  }
}

/** Resident list multi-edit uses lifecycle statuses only (NOT_STARTED, ACTIVE, CLOSED). */
export function getResidentMultiEditStatusMapping(
  mappings?: ResidentStatusListMappings | null,
): NonNullable<RentalListResponse['rental_status']> {
  return { ...(mappings?.resident_status_mapping ?? {}) }
}

/** Resolve claim status code to display label with Angular role-based rewrites. */
export function formatClaimStatusLabel(
  claimStatus: string | null | undefined,
  options: {
    roleName?: string
    isLiberty?: boolean
    labels?: Record<string, string>
  } = {},
): string {
  const code = String(claimStatus ?? '').trim()
  if (!code) {
    return lookupConstantLabel(options.labels, 'NOT_STARTED', 'Not Started')
  }

  const isLiberty =
    options.isLiberty ??
    (options.roleName
      ? (RESIDENT_LIBERTY_ROLES as readonly string[]).includes(options.roleName)
      : false)

  if (
    !isLiberty &&
    NON_LIBERTY_COLLAPSED_CLAIM_STATUS_CODES.includes(
      code as (typeof NON_LIBERTY_COLLAPSED_CLAIM_STATUS_CODES)[number],
    )
  ) {
    return lookupConstantLabel(options.labels, 'LIBERTY_REVIEW', 'Liberty Review')
  }

  const mapped = lookupConstantLabel(options.labels, code, '')
  if (!mapped || mapped === code) {
    return lookupConstantLabel(options.labels, 'NOT_STARTED', 'Not Started')
  }

  if (isLiberty && (code === 'EXTERNAL_REVIEW' || mapped === 'External Review')) {
    return 'Level 3 Review'
  }

  return mapped
}

/** Filter / select options for claim status; liberty users see Level 3 Review for EXTERNAL_REVIEW. */
export function claimStatusSelectOptions(
  labels?: Record<string, string>,
  options?: { isLiberty?: boolean },
): SelectOption[] {
  const base = constantsToSelectOptions(labels)
  if (!options?.isLiberty) return base

  return base.map((option) =>
    option.value === 'EXTERNAL_REVIEW' ? { ...option, label: 'Level 3 Review' } : option,
  )
}
